import React, { useEffect, useMemo, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson, appFetchJson } from "../../../utils/http";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";

/* =========================
   HELPERS
========================= */
function toBackendDate(isoDate) {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
}

/**
 * Props:
 * - id: ID de la Liquidación Ejecución
 * - onClose / onVolver
 * - onSaved
 */
export default function EditarLiquidacionSemanas({
  id,
  onClose,
  onVolver,
  onSaved,
}) {
  const closeFn = onClose || onVolver || (() => {});
  const savedFn = onSaved || (() => {});

  // ============================
  // ESTADOS
  // ============================
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [original, setOriginal] = useState(null);

  // ===== EDITABLES =====
  const [dataFechafin, setDataFechafin] = useState("");
  const [totalIvaCliente, setTotalIvaCliente] = useState("");
  const [totalIvaEess, setTotalIvaEess] = useState("");
  const [valorTotalDescLibe, setValorTotalDescLibe] = useState("");
  const [qnFactura, setQnFactura] = useState("");
  const [qnNotaCredito, setQnNotaCredito] = useState("");
  const [qnClieSinFacturar, setQnClieSinFacturar] = useState("");

  // ============================
  // ESC
  // ============================
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeFn();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeFn]);

  // ============================
  // CARGA INICIAL
  // ============================
  useEffect(() => {
    if (!id) return;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const dto = await apiFetchJson(
          apiRoutes.liquidacionEjecucion.detail(id)
        );

        setOriginal(dto);

        // ⚠️ dataFechafin llega como yyyy-MM-dd (response)
        setDataFechafin(dto.dataFechafin ?? "");

        setTotalIvaCliente(dto.totalIvaCliente ?? "");
        setTotalIvaEess(dto.totalIvaEess ?? "");
        setValorTotalDescLibe(dto.valorTotalDescLibe ?? "");
        setQnFactura(dto.qnFactura ?? "");
        setQnNotaCredito(dto.qnNotaCredito ?? "");
        setQnClieSinFacturar(dto.qnClieSinFacturar ?? "");
      } catch (e) {
        setError(e?.message || "Error al cargar la Liquidación");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  // ============================
  // CAMBIO LEGACY
  // ============================
  const changed = useMemo(() => {
    if (!original) return false;
    return dataFechafin !== (original.dataFechafin ?? "");
  }, [original, dataFechafin]);

  // ============================
  // GUARDAR
  // ============================
  const handleSave = async () => {
    if (!changed || !id) return;

    setSaving(true);
    setError("");

    try {
      await appFetchJson(
        apiRoutes.liquidacionEjecucion.update(id), // 👈 NO detail
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
            id,
            dataFechafin: toBackendDate(dataFechafin),
            valorTotalCliente: totalIvaCliente,
            valorTotalEess: totalIvaEess,
            valorTotalDescLibe,
            qnFactura,
            qnNotaCredito,
            qnClieSinFacturar,
            }),
        },
        { showSuccess: true }
    );

      savedFn();
      closeFn();
    } catch (e) {
      setError(e?.message || "Ocurrió un error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // ============================
  // RENDER
  // ============================
  if (loading) {
    return (
      <div className="modal-backdrop">
        <div className="modal-panel-lg">
          <div className="modal-body">Cargando…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={closeFn}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="section-title">Editar Liquidación</h2>
        </div>

        <div className="modal-body">
          {error && <div className="mb-3 text-red-600">{error}</div>}

          <div className="form-grid">
            {/* READ ONLY */}
            <FilaRO label="Código" value={original.codigoLiquidacion} />
            <FilaRO label="Estado" value={original.estadoNombre} />
            <FilaRO label="Liquidación" value={original.liquidacionNombre} />
            <FilaRO label="Fecha Inicio" value={original.dataFechaini} />

            {/* EDITABLES */}
            <FilaEdit
              label="Fecha Final"
              input={
                <input
                  type="date"
                  className="input"
                  value={dataFechafin}
                  onChange={(e) => setDataFechafin(e.target.value)}
                />
              }
            />

            <FilaEdit label="Total IVA Cliente" input={<InputNum v={totalIvaCliente} s={setTotalIvaCliente} />} />
            <FilaEdit label="Total IVA EESS" input={<InputNum v={totalIvaEess} s={setTotalIvaEess} />} />
            <FilaEdit label="Liberación" input={<InputNum v={valorTotalDescLibe} s={setValorTotalDescLibe} />} />
            <FilaEdit label="Cantidad Factura" input={<InputNum v={qnFactura} s={setQnFactura} />} />
            <FilaEdit label="Cantidad Nota Crédito" input={<InputNum v={qnNotaCredito} s={setQnNotaCredito} />} />
            <FilaEdit label="Cant. Clie. Sin Facturar" input={<InputNum v={qnClieSinFacturar} s={setQnClieSinFacturar} />} />
          </div>
        </div>

        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar isValid={changed} saving={saving} onClick={handleSave} />
            <BtnCancelar onClick={closeFn} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   SUBCOMPONENTES
========================= */

function FilaEdit({ label, input }) {
  return (
    <div className="form-row gap-2 items-start">
      <div className="form-label">{label}:</div>
      <div className="flex-1">{input}</div>
    </div>
  );
}

function FilaRO({ label, value }) {
  return (
    <div className="form-row gap-2 items-start">
      <div className="form-label">{label}:</div>
      <div className="flex-1 text-gray-600">{value ?? "-"}</div>
    </div>
  );
}

function InputNum({ v, s }) {
  return (
    <input
      type="number"
      className="input"
      value={v}
      onChange={(e) => s(e.target.value)}
    />
  );
}
